import React, { useState } from 'react';
import { FaMapMarkerAlt, FaRuler, FaSearch, FaSync, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import LeafletMap from '../components/MapComponents/LeafletMap';
import Seo from '../components/Common/Seo';

const TestMapAccuracy = () => {
  const [selectedTest, setSelectedTest] = useState(null);
  const [discrepancy, setDiscrepancy] = useState(0);
  const [loading, setLoading] = useState(false);

  // Known South African routes with Google Maps distances
  const knownRoutes = [
    {
      id: 1,
      name: 'Johannesburg → Cape Town',
      start: 'Johannesburg',
      end: 'Cape Town',
      googleDistance: 1398, // km from Google Maps
      geometry: [] // Will be populated from Mapbox API
    },
    {
      id: 2,
      name: 'Johannesburg → Durban',
      start: 'Johannesburg',
      end: 'Durban',
      googleDistance: 567,
      geometry: []
    },
    {
      id: 3,
      name: 'Cape Town → Port Elizabeth',
      start: 'Cape Town',
      end: 'Port Elizabeth',
      googleDistance: 750,
      geometry: []
    },
    {
      id: 4,
      name: 'Pretoria → Bloemfontein',
      start: 'Pretoria',
      end: 'Bloemfontein',
      googleDistance: 398,
      geometry: []
    }
  ];

  // REAL TEST: Fetch actual Mapbox route data
  const handleTestRoute = async (route) => {
    setLoading(true);
    setSelectedTest(route);
    
    try {
      // Call YOUR backend API to get REAL Mapbox data
      const response = await fetch(
        `http://localhost:8000/get_route?start=${route.start}&end=${route.end}&vehicle_class=Class 1&fuel_type=Petrol&avg_consumption=8`
      );
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.routes || data.routes.length === 0) {
        throw new Error('No routes returned from API');
      }
      
      // Use the FIRST (fastest) route
      const mapboxRoute = data.routes[0];
      const ourDistance = parseFloat(mapboxRoute.total_distance_km.replace(' km', ''));
      const discrepancy = ((ourDistance - route.googleDistance) / route.googleDistance * 100).toFixed(1);
      
      // Store the REAL geometry from Mapbox
      const geometry = mapboxRoute.route_geometry || [];
      
      // Update the route with REAL data
      const updatedRoute = {
        ...route,
        geometry: geometry,
        ourDistance: ourDistance,
        points: geometry.length,
        mapboxDistance: `${ourDistance} km`
      };
      
      setSelectedTest(updatedRoute);
      setDiscrepancy(discrepancy);
      
      console.log('📊 REAL Route Test:', {
        route: route.name,
        googleDistance: `${route.googleDistance} km`,
        mapboxDistance: `${ourDistance} km`,
        discrepancy: `${discrepancy}%`,
        points: geometry.length,
        isAccurate: Math.abs(discrepancy) < 10
      });
      
    } catch (error) {
      console.error('❌ Test failed:', error);
      
      // Fallback to simple geometry if API fails
      const fallbackGeometry = getFallbackGeometry(route);
      const ourDistance = calculateDistance(fallbackGeometry);
      const discrepancy = ((ourDistance - route.googleDistance) / route.googleDistance * 100).toFixed(1);
      
      const updatedRoute = {
        ...route,
        geometry: fallbackGeometry,
        ourDistance: ourDistance,
        points: fallbackGeometry.length,
        mapboxDistance: `${ourDistance} km`,
        error: 'Using fallback data - API failed'
      };
      
      setSelectedTest(updatedRoute);
      setDiscrepancy(discrepancy);
      
      alert(`API Error: ${error.message}. Using fallback data for testing.`);
    } finally {
      setLoading(false);
    }
  };

  // Fallback geometry for API failures
  const getFallbackGeometry = (route) => {
    const fallbacks = {
      'Johannesburg → Cape Town': [
        [-26.2041, 28.0473], [-27.5, 28.5], [-29.0, 29.0], [-30.5, 27.5],
        [-32.0, 24.0], [-33.0, 21.0], [-33.9249, 18.4241]
      ],
      'Johannesburg → Durban': [
        [-26.2041, 28.0473], [-27.0, 29.0], [-28.0, 30.0], [-29.0, 30.5],
        [-29.8587, 31.0218]
      ],
      'Cape Town → Port Elizabeth': [
        [-33.9249, 18.4241], [-34.0, 20.0], [-34.5, 22.0], [-34.0, 24.0],
        [-33.9608, 25.6022]
      ],
      'Pretoria → Bloemfontein': [
        [-25.7479, 28.2293], [-26.5, 28.0], [-27.5, 27.0], [-28.5, 26.5],
        [-29.0852, 26.1596]
      ]
    };
    return fallbacks[route.name] || [];
  };

  // Distance calculation for fallback data
  const calculateDistance = (coords) => {
    if (!coords || coords.length < 2) return 0;
    
    let total = 0;
    for (let i = 1; i < coords.length; i++) {
      const [lat1, lng1] = coords[i-1];
      const [lat2, lng2] = coords[i];
      
      const R = 6371000;
      const φ1 = lat1 * Math.PI/180;
      const φ2 = lat2 * Math.PI/180;
      const Δφ = (lat2-lat1) * Math.PI/180;
      const Δλ = (lng2-lng1) * Math.PI/180;
      
      const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ/2) * Math.sin(Δλ/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      total += R * c;
    }
    return (total / 1000).toFixed(1);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <Seo
        title="Map Accuracy Test | Whe2"
        description="Internal route accuracy diagnostics for Whe2."
        path="/health"
        robots="noindex,nofollow"
      />
      <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <FaRuler /> Map Accuracy Test
      </h1>
      
      <div style={{ 
        background: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '10px',
        marginBottom: '20px'
      }}>
        <h3>How to Test:</h3>
        <ol style={{ marginLeft: '20px' }}>
          <li>Select a test route below</li>
          <li>We fetch <strong>REAL Mapbox API data</strong> from your backend</li>
          <li>Compare Mapbox distance vs Google Maps distance</li>
          <li>See the <strong>actual route geometry</strong> on the map</li>
          <li>Check discrepancy percentage (aim for &lt;10%)</li>
        </ol>
        <p style={{ color: '#666', marginTop: '10px' }}>
          <strong>Note:</strong> This tests your <strong>actual app's accuracy</strong> using real Mapbox API data, 
          not simplified test geometry. Your backend must be running on <code>localhost:8000</code>.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Test Routes List */}
        <div>
          <h3>Test Routes</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {knownRoutes.map(route => (
              <button
                key={route.id}
                onClick={() => handleTestRoute(route)}
                disabled={loading}
                style={{
                  padding: '15px',
                  background: selectedTest?.id === route.id ? '#e3f2fd' : 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  textAlign: 'left',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  opacity: loading ? 0.7 : 1
                }}
              >
                <div>
                  <strong>{route.name}</strong>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                    Google Maps: {route.googleDistance} km
                  </div>
                </div>
                {loading && selectedTest?.id === route.id ? (
                  <FaSync className="spinner" style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <FaSearch />
                )}
              </button>
            ))}
          </div>

          {/* Results Display */}
          {selectedTest && (
            <div style={{
              marginTop: '20px',
              padding: '20px',
              background: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px'
            }}>
              <h3>Test Results: {selectedTest.name}</h3>
              
              {selectedTest.error && (
                <div style={{
                  padding: '10px',
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  marginBottom: '15px',
                  color: '#856404'
                }}>
                  ⚠️ {selectedTest.error}
                </div>
              )}
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '15px' }}>
                <div style={{ padding: '10px', background: '#f0f9ff', borderRadius: '6px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Google Maps</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e88e5' }}>
                    {selectedTest.googleDistance} km
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Reference distance
                  </div>
                </div>
                <div style={{ padding: '10px', background: '#f0f9ff', borderRadius: '6px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Mapbox API</div>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: selectedTest.ourDistance > selectedTest.googleDistance ? '#e53935' : '#43a047' 
                  }}>
                    {selectedTest.ourDistance} km
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    {selectedTest.points} route points
                  </div>
                </div>
              </div>
              
              <div style={{
                marginTop: '15px',
                padding: '15px',
                background: Math.abs(discrepancy) > 10 ? '#ffebee' : '#e8f5e8',
                borderRadius: '6px',
                border: `2px solid ${Math.abs(discrepancy) > 10 ? '#e53935' : '#43a047'}`
              }}>
                <div style={{ fontSize: '14px', color: '#666' }}>Discrepancy</div>
                <div style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold',
                  color: Math.abs(discrepancy) > 10 ? '#e53935' : '#43a047',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  {Math.abs(discrepancy) > 10 ? (
                    <FaExclamationTriangle />
                  ) : (
                    <FaCheckCircle />
                  )}
                  {discrepancy}%
                  {Math.abs(discrepancy) > 10 ? ' ⚠️ High' : ' ✓ Good'}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {Math.abs(discrepancy) > 10 
                    ? 'Mapbox route differs significantly from Google'
                    : 'Accuracy is within acceptable range'}
                </div>
              </div>

              <div style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
                <strong>Route Details:</strong>
                <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
                  <li>Points in route: {selectedTest.points}</li>
                  <li>Difference: {(selectedTest.ourDistance - selectedTest.googleDistance).toFixed(1)} km</li>
                  <li>Data Source: {selectedTest.error ? 'Fallback Geometry' : 'Mapbox API (Real)'}</li>
                  <li>Test Status: {loading ? 'Testing...' : 'Complete'}</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Map Display */}
        <div>
          <h3>Route Visualization</h3>
          <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
            {selectedTest && selectedTest.geometry && selectedTest.geometry.length > 0 ? (
              <div style={{ height: '600px' }}>
                <LeafletMap
                  routeGeometry={selectedTest.geometry}
                  tollgates={[]}
                  tripName={selectedTest.name}
                  height="600px"
                />
                <div style={{ 
                  padding: '10px', 
                  background: '#f8f9fa',
                  borderTop: '1px solid #ddd',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <strong>Map Legend:</strong> Blue line = {selectedTest.error ? 'Fallback Route' : 'Mapbox API Route'}, 
                  Green = Start, Red = End
                  {selectedTest.error && ' (⚠️ Using fallback due to API error)'}
                </div>
              </div>
            ) : (
              <div style={{
                height: '600px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8f9fa',
                color: '#999',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {loading ? (
                  <>
                    <FaSync className="spinner" style={{ animation: 'spin 1s linear infinite', fontSize: '24px' }} />
                    <div>Fetching real Mapbox data...</div>
                  </>
                ) : (
                  <>
                    <FaMapMarkerAlt size={24} />
                    <div>Select a test route to display REAL Mapbox route</div>
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Instructions for Google Maps Comparison */}
          {selectedTest && (
            <div style={{
              marginTop: '15px',
              padding: '15px',
              background: '#fff8e1',
              border: '1px solid #ffd54f',
              borderRadius: '8px'
            }}>
              <h4 style={{ marginTop: 0 }}>How to Compare with Google Maps:</h4>
              <ol style={{ marginLeft: '20px', marginBottom: 0 }}>
                <li>Open <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer">Google Maps</a></li>
                <li>Search: "{selectedTest.start} to {selectedTest.end}"</li>
                <li>Check the driving distance shown by Google</li>
                <li>Compare with Mapbox calculation above ({selectedTest.ourDistance} km)</li>
                <li>Visually compare the route shapes</li>
              </ol>
            </div>
          )}
        </div>
      </div>

      {/* Accuracy Assessment */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        background: 'white',
        border: '1px solid #ddd',
        borderRadius: '10px'
      }}>
        <h3>Mapbox API Accuracy Assessment</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginTop: '15px' }}>
          <div style={{ padding: '15px', background: '#f5f5f5', borderRadius: '6px' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>Excellent</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#43a047' }}>&lt; 5%</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Mapbox is very accurate</div>
          </div>
          <div style={{ padding: '15px', background: '#f5f5f5', borderRadius: '6px' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>Good</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fb8c00' }}>5-10%</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Normal for different mapping services</div>
          </div>
          <div style={{ padding: '15px', background: '#f5f5f5', borderRadius: '6px' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>Needs Review</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e53935' }}>&gt; 10%</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Check Mapbox routing profile or API parameters
            </div>
          </div>
        </div>
        
        <div style={{ marginTop: '20px', padding: '15px', background: '#e3f2fd', borderRadius: '8px' }}>
          <h4>What This Test Actually Measures:</h4>
          <ul style={{ marginLeft: '20px' }}>
            <li><strong>Your backend's Mapbox API integration</strong> - real data, not test data</li>
            <li><strong>Mapbox routing accuracy</strong> vs Google Maps as reference</li>
            <li><strong>Actual route geometry</strong> with hundreds of points following roads</li>
            <li><strong>Real-world performance</strong> of your complete app stack</li>
          </ul>
        </div>
      </div>

      {/* Add CSS for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default TestMapAccuracy;
